<template>
  <v-card
    :class="$style.purchase_order_widget"
    :loading="order_sides_loading"
  >
    <div :class="$style.widget_title">
      <span class="display-3">Заказ-наряды</span>

      <v-tooltip
        color="primary darken-3"
        :content-class="$style.commentTooltip"
        right
      >
        <template v-slot:activator="{ on, attrs }">
          <v-icon
            :class="[$style.icon, 'ml-2']"
            v-bind="attrs"
            v-on="on"
          >
            mdi-information-outline
          </v-icon>
        </template>
        <span>В виджете отображаются данные из заказ-нарядов,которые находятся в статусах:  Ожидание диагностики/ Проведение диагностики/ Ожидание согласования/ Ожидание ЗИП/ Ремонт</span>
      </v-tooltip>

    </div>

    <template v-if="is_filters">
      <div class="d-flex mb-6">
        <v-row>
          <v-col
            cols="12"
            xl="4"
            lg="6"
            md="12"
            sm="6"
            xs="12"
          >
            <DatepickerRange
              :date_prop="filters.order_side_dates"
              label="Период создания"
              type_date='period'
              :class="[$style.datepicker, $style.filterField, $style.datepickerMargin]"
              dense
              clearable
              range
              small_info
              @date_changed="handle_change_dates"
            />

          </v-col>
          <v-col
            cols="12"
            md="12"
            lg="6"
            xl="4"
            sm="6"
            xs="12"
          >
            <Select
              :value="filters.order_side_organization_id"
              :items="orgs"
              :class="[$style.select, $style.filterField, 'mb-4']"
              label="Организация"
              item-text="name_organization"
              item_value="id"
              multiple
              dense
              hide-details
              clearable
              blue_icon
              @input="filter_update('order_side_organization_id', $event, 'order_sides')"
            />
          </v-col>
          <v-col
            cols="12"
            md="12"
            lg="6"
            xl="4"
            sm="6"
            xs="12"
          >
            <Select
              :value="filters.order_side_client_id"
              :items="clients"
              :class="[$style.select, $style.filterField, 'mb-4']"
              label="Клиент"
              item-text="name_client_company"
              item_value="client_id"
              dense
              hide-details
              clearable
              blue_icon
              @input="filter_update('order_side_client_id', $event, 'order_sides')"
            />
          </v-col>
          <v-col
            cols="12"
            md="12"
            lg="6"
            xl="4"
            sm="6"
            xs="12"
          >
            <Select
              :value="filters.order_side_operation_type_id"
              :items="operation_types"
              :class="[$style.select, $style.filterField, 'mb-4']"
              label="Вид операции"
              item-text="name"
              item_value="id"
              multiple
              dense
              hide-details
              clearable
              blue_icon
              @input="filter_update('order_side_operation_type_id', $event, 'order_sides')"
            />
          </v-col>
          <v-col
            cols="12"
            md="12"
            lg="6"
            xl="4"
            sm="6"
            xs="12"
          >
            <Select
              :value="filters.repair_type_id"
              :items="repair_types"
              :class="[$style.select, $style.filterField, 'mb-4']"
              label="Вид ремонта"
              item-text="name"
              item_value="id"
              multiple
              dense
              hide-details
              clearable
              blue_icon
              @input="filter_update('repair_type_id', $event, 'order_sides')"
            />
          </v-col>
        </v-row>
      </div>
    </template>

    <template v-if="is_filters">
      <div
        v-for="item in analytics_orders_sides"
        :key="item.title"
        class="align-start"
        :class="$style.widget_item"
      >
        <span
          :class="$style.widget_cert_text"
          class="headline font-weight-regular"
        >
          {{ item.title }}
        </span>
        <span class="headline font-weight-bold">{{ item.value }}</span>
      </div>
    </template>
    <template v-else>
      <div
        v-for="item in orders"
        :key="item.title"
        class="align-start"
        :class="$style.widget_item"
      >
        <span
          :class="$style.widget_cert_text"
          class="headline font-weight-regular"
        >
          {{ item.title }}
        </span>
        <span class="headline font-weight-bold">{{ item.value }}</span>
      </div>
    </template>
  </v-card>
</template>

<script lang="ts">
import {
  Vue, Component, Prop, Emit,
} from 'vue-property-decorator';
import { widgets_store, analytics_store } from '@/store';
import { Select, DatepickerRange } from '@/components/global';
import { AnalyticsFilters } from '@/modules/analytics/types';

const { state } = widgets_store;

const analytics_state = analytics_store.state;

@Component({
  components: {
    apexchart: () => import('vue-apexcharts'),
    Select,
    DatepickerRange,
  },
})
export default class WidgetOrderSides extends Vue {
  @Prop({ type: Boolean, default: false })
  is_filters!: boolean;

  @Emit()
  filter_update(key: keyof AnalyticsFilters, value: unknown, section: string) {
    analytics_store.mutations.UPD_FILTER_KEY({ key, value, section });
  }

  get orders() {
    return state.widgets.order_sides?.items;
  }

  get analytics_orders_sides() {
    const response_filters = this.has_response_filters();

    return response_filters
      ? analytics_state.response_filters.order_sides.items
      : analytics_state.analytics.order_sides.items;
  }

  get operation_types() {
    return analytics_state.operation_types;
  }

  get repair_types() {
    return analytics_state.repair_types;
  }

  get orgs() {
    return analytics_state.organizations;
  }

  get clients() {
    return analytics_state.clients;
  }

  get filters() {
    return analytics_state.filters;
  }

  get order_sides_loading() {
    return analytics_state.order_sides_loading;
  }

  handle_change_dates(dates: string[]) {
    const dates_unformatted = [...dates];
    const dates_formatted = dates_unformatted.map((date) => new Date(date));

    if (dates_formatted[1] < dates_formatted[0]) {
      [
        dates_unformatted[0], dates_unformatted[1],
      ] = [
        dates[1], dates[0],
      ];
    }
    this.filter_update('order_side_dates', dates_unformatted, 'order_sides');
  }

  has_response_filters() {
    return (
      analytics_state.filters.order_side_client_id !== null
      || analytics_state.filters.repair_type_id.length > 0
      || analytics_state.filters.order_side_dates.length > 0
      || analytics_state.filters.order_side_operation_type_id.length > 0
      || analytics_state.filters.order_side_organization_id.length > 0
    );
  }
}
</script>

<style module lang="scss">
@import '@/scss/common/filter-blocks.scss';

.purchase_order_widget {
  // height: 300px;
  display: flex;
  flex-direction: column;
  padding: 24px;
}
.widget_title {
  display: flex;
  margin-bottom: 24px;
  font-style: 24px;
  font-weight: 600;
  line-height: 32px;
  color: #13141C;
}
.widget_item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
}
.widget_item:nth-child(odd){
  background-color: #F6F7FA;
  border-radius: 8px;
}
.widget_cert_text {
  width: 192px;
}

.filterField {
  max-width: 100% !important;
}

.datepickerMargin {
  margin-bottom: -14px;
}

.commentTooltip {
  max-width: 500px;
  text-align: left;
}
</style>
